import { useQuery } from '@tanstack/react-query'
import { fetchFieldMap, type FieldMapDetail } from '../api/fieldMap'
import type {
  FieldMapResponse,
  TensionNodeProps,
  SectionNodeProps,
  BlockNodeProps,
} from '../types/fieldMap'

interface UseFieldMapOptions {
  postId: number | null
  detail?: FieldMapDetail
  /** Polling interval in ms while the editor is active. 0 disables polling. */
  refetchInterval?: number
  enabled?: boolean
}

/**
 * Subscribe a component to a post's field_map. Backed by React Query so
 * multiple consumers (SalienceOverlay, FieldSalienceDecoration, future
 * CanvasView, etc.) share one in-flight fetch and one cache entry per
 * (postId, detail) pair.
 *
 * Default detail is `'metrics'` — superset of `'compact'` that adds
 * tension nodes, density/potential on section nodes, and a top-level
 * `metrics` aggregate. Per Content's IACP `beb2f79d` (verified in
 * `field_map.py:560-660`), `'full'` does NOT include metrics or
 * tensions — it adds debug.paragraphs only. Salience consumers must
 * use `'metrics'`.
 *
 * Default polling 7s. A `field_map_updated` push event is a Phase 5+
 * additive option when Canvas-View needs reactive Salience without
 * polling latency.
 */
export function useFieldMap({
  postId,
  detail = 'metrics',
  refetchInterval = 7000,
  enabled = true,
}: UseFieldMapOptions) {
  return useQuery<FieldMapResponse>({
    queryKey: ['fieldMap', postId, detail],
    queryFn: () => fetchFieldMap(postId!, detail),
    enabled: enabled && postId !== null,
    refetchInterval,
    staleTime: refetchInterval / 2,
  })
}

/**
 * Open-question tensions from the field_map. In 'metrics' mode both
 * `data.metrics.open_questions` (compact aggregate) and tension nodes
 * inside `data.nodes` are populated; the aggregate is faster to read
 * so we prefer it and fall back to the node walk only if metrics is
 * absent (e.g. caller passed `detail='compact'`).
 */
export function selectTensions(data: FieldMapResponse | undefined): TensionNodeProps[] {
  if (!data) return []
  if (data.metrics?.open_questions?.length) {
    return data.metrics.open_questions.map((q) => ({
      kind: 'open_question',
      method: 'question_mark_heuristic',
      block_index: q.block_index,
      author_id: q.author_id,
      text: q.text,
      resolution_status: 'unresolved',
      derived_from: [`block:${q.block_index}`],
    }))
  }
  return data.nodes
    .filter((n): n is { id: string; type: 'tension'; properties: TensionNodeProps } =>
      n.type === 'tension',
    )
    .map((n) => n.properties)
}

/**
 * Sections where one author dominates (potential Echo-Verstärkung).
 * Threshold: `dominance_score >= 0.7` with `n_authors >= 2` — single-
 * author sections aren't dominance, they're just one voice and don't
 * need anti-Echo cues.
 */
export function selectDominatedSections(
  data: FieldMapResponse | undefined,
): Array<{ section: SectionNodeProps; dominance_score: number; dominant_author_id: string }> {
  if (!data) return []
  return data.nodes
    .filter(
      (n): n is { id: string; type: 'section'; properties: SectionNodeProps } =>
        n.type === 'section' && n.properties.potential != null,
    )
    .filter((n) => {
      const p = n.properties.potential!
      const d = n.properties.density
      return p.dominance_score >= 0.7 && (d?.n_authors ?? 0) >= 2
    })
    .map((n) => ({
      section: n.properties,
      dominance_score: n.properties.potential!.dominance_score,
      dominant_author_id: n.properties.potential!.dominant_author_id,
    }))
}

/**
 * Paragraph indices that the field_map flagged as tensions. Used by
 * `FieldSalienceDecoration` to glow the matching paragraphs in the
 * editor — anti-Echo cue at the source of the question itself, so
 * the linear-reading bias doesn't bury them.
 */
export function selectTensionParagraphIndices(
  data: FieldMapResponse | undefined,
): Set<number> {
  const out = new Set<number>()
  for (const t of selectTensions(data)) out.add(t.block_index)
  return out
}

/**
 * Paragraph indices that belong to dominated sections. We dim those
 * paragraphs (very lightly) as a visual nudge: this voice has
 * crowded the section, the others deserve room.
 */
export function selectDominatedParagraphIndices(
  data: FieldMapResponse | undefined,
): Set<number> {
  const out = new Set<number>()
  if (!data) return out
  const dominatedSectionIds = new Set(
    selectDominatedSections(data).map((d) => `section:${d.section.slug}`),
  )
  if (dominatedSectionIds.size === 0) return out
  for (const n of data.nodes) {
    if (n.type !== 'block') continue
    const props = n.properties as BlockNodeProps
    if (props.in_section_id && dominatedSectionIds.has(props.in_section_id)) {
      out.add(props.paragraph_index)
    }
  }
  return out
}
